import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ItemCard } from '../../types/pointItemNote';
import type { ClassMemberRow } from '../../utils/classMembersStorage';
import MemberProfileThumb from '../common/MemberProfileThumb';
import ProfileCardCachedImage from '../common/ProfileCardCachedImage';
import ConfettiBurst from '../common/ConfettiBurst';
import { SeatRotateIcon } from '../common/SeatRotateIcon';
import type { AdminProfileCardSetWithSlots, ProfileCardSetSource } from '../../types/profileCard';
import { profileCardSetKey, profileCardSlotDisplayName } from '../../types/profileCard';
import { resolveItemCardImageSrc } from '../../utils/itemCardImage';

export type StoreProfileCardSet = AdminProfileCardSetWithSlots & {
  priceCoins: number;
  source: ProfileCardSetSource;
};

export type ProfileCardPurchaseResult =
  | {
      ok: true;
      storagePath: string;
      imageVersion: number;
      source: ProfileCardSetSource;
      slotIndex: number;
      cardName: string;
    }
  | { ok: false; reason: 'INSUFFICIENT_COINS' | 'ALL_UNLOCKED' };

type Props = {
  open: boolean;
  storeItems: ItemCard[];
  currentCoins: number;
  memberLabel?: string;
  onClose: () => void;
  /** 구매하기 → 부모에서 드래프트로 등록 (즉시 저장하지 않음) */
  onPurchase: (card: ItemCard) => void;
  /** 장바구니 담기 → 아이템 탭으로 전환하고 데이터 기록 모드 활성화 */
  onAddToCart?: (card: ItemCard) => void;
  /** 클래스 스토어 장바구니 담기 → 선택된 멤버들에게 담기 */
  onAddToCartMany?: (card: ItemCard, memberIds: string[]) => void;
  /** 스토어에서 활성화된 프로필 카드 세트(가격 포함) */
  profileSets?: StoreProfileCardSet[];
  /** 프로필 카드 구매 → 즉시 랜덤 뽑기 + DB 즉시 반영 (중복 뽑기 가능) */
  onPurchaseProfileCard?: (set: StoreProfileCardSet) => ProfileCardPurchaseResult;
  /** 열릴 때 기본 탭 (기본: item) */
  initialTab?: 'item' | 'profile';
  /** true면 프로필 카드 구입만 표시 */
  profileOnly?: boolean;
  /** 클래스 스토어: 구매자 후보(있으면 복수 선택 후 일괄 구매) */
  buyers?: StoreBuyerOption[];
  workspaceId?: string;
  gradeId?: string;
  onPurchaseMany?: (card: ItemCard, memberIds: string[]) => void;
  onPurchaseProfileCardMany?: (
    set: StoreProfileCardSet,
    memberIds: string[]
  ) => { okCount: number; lastOk: Extract<ProfileCardPurchaseResult, { ok: true }> | null };
};

export type StoreBuyerOption = {
  id: string;
  name: string;
  orderIndex: number;
  coins: number;
  member: Pick<
    ClassMemberRow,
    'profileImage' | 'photoProfilePath' | 'photoProfileVersion' | 'name' | 'seatPosition'
  >;
};

const STORE_SEAT_EMPTY = '자리배치된 멤버가 없습니다. 번호 보기를 사용하세요.';

function buyerHasSeat(buyer: StoreBuyerOption): boolean {
  const pos = buyer.member.seatPosition;
  return !!pos && typeof pos.row === 'number' && typeof pos.col === 'number';
}

function buildStoreBuyerSeatView(buyers: StoreBuyerOption[], rotated: boolean) {
  const seated = buyers.filter(buyerHasSeat);
  const unseated = buyers.filter((b) => !buyerHasSeat(b));
  if (!seated.length) {
    return {
      positioned: [] as Array<{ buyer: StoreBuyerOption; gridRow: number; gridCol: number }>,
      unseated,
      rowTemplate: '',
      colTemplate: '',
    };
  }

  const GRID_TRACKS = 16;
  const ROTATE_MAX_INDEX = 14;
  const usedRows = new Set<number>();
  const usedCols = new Set<number>();

  const rotatedSeats = seated.map((buyer) => {
    const seatRow = buyer.member.seatPosition!.row;
    const seatCol = buyer.member.seatPosition!.col;
    const rotatedRow = rotated ? ROTATE_MAX_INDEX - seatRow : seatRow;
    const rotatedCol = rotated ? ROTATE_MAX_INDEX - seatCol : seatCol;
    [rotatedRow, rotatedRow + 1].forEach((r) => {
      if (r >= 0 && r < GRID_TRACKS) usedRows.add(r);
    });
    [rotatedCol, rotatedCol + 1].forEach((c) => {
      if (c >= 0 && c < GRID_TRACKS) usedCols.add(c);
    });
    return { buyer, rotatedRow, rotatedCol };
  });

  const trimStartRow = Math.min(...Array.from(usedRows));
  const trimEndRow = Math.max(...Array.from(usedRows));
  const trimStartCol = Math.min(...Array.from(usedCols));
  const trimEndCol = Math.max(...Array.from(usedCols));

  const rowTemplate = Array.from(
    { length: Math.max(1, trimEndRow - trimStartRow + 1) },
    (_, idx) => (usedRows.has(trimStartRow + idx) ? '1fr' : '0.5fr')
  ).join(' ');

  const colTemplate = Array.from(
    { length: Math.max(1, trimEndCol - trimStartCol + 1) },
    (_, idx) => (usedCols.has(trimStartCol + idx) ? '1fr' : '0.28fr')
  ).join(' ');

  const positioned = rotatedSeats.map(({ buyer, rotatedRow, rotatedCol }) => ({
    buyer,
    gridRow: rotatedRow - trimStartRow + 1,
    gridCol: rotatedCol - trimStartCol + 1,
  }));

  return { positioned, unseated, rowTemplate, colTemplate };
}

const ProfileSetCoverImg: React.FC<{
  source: ProfileCardSetSource;
  storagePath: string;
  imageVersion: number;
}> = ({ source, storagePath, imageVersion }) => (
  <ProfileCardCachedImage
    source={source}
    storagePath={storagePath}
    imageVersion={imageVersion}
    alt=""
  />
);

const CoinIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="10" fill="#f39c12" />
    <circle cx="12" cy="12" r="8" fill="#f1c40f" />
    <text
      x="12"
      y="16"
      fontSize="12"
      fontWeight="bold"
      fill="#e67e22"
      textAnchor="middle"
    >
      ₩
    </text>
  </svg>
);

const CardPlaceholder: React.FC<{ size?: number }> = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect
      x="2"
      y="6"
      width="12"
      height="16"
      rx="2"
      fill="#f0f4f3"
      stroke="#555"
      strokeWidth="1.5"
      transform="rotate(-10 8 14)"
    />
    <rect
      x="6"
      y="4"
      width="12"
      height="16"
      rx="2"
      fill="#f8f8f8"
      stroke="#555"
      strokeWidth="1.5"
    />
    <rect
      x="10"
      y="6"
      width="12"
      height="16"
      rx="2"
      fill="#fafafa"
      stroke="#555"
      strokeWidth="1.5"
      transform="rotate(10 16 14)"
    />
  </svg>
);

const MemberStoreModal: React.FC<Props> = ({
  open,
  storeItems,
  currentCoins,
  memberLabel,
  onClose,
  onPurchase,
  onAddToCart,
  onAddToCartMany,
  profileSets = [],
  onPurchaseProfileCard,
  initialTab = 'item',
  profileOnly = false,
  buyers = [],
  workspaceId = '',
  gradeId = '',
  onPurchaseMany,
  onPurchaseProfileCardMany,
}) => {
  const classWide = buyers.length > 0;
  /** 클래스(복수 멤버) 스토어에서는 프로필 카드 카테고리를 숨긴다 */
  const hideProfileCategory = classWide;
  const [activeTab, setActiveTab] = useState<'item' | 'profile'>(
    profileOnly && !hideProfileCategory ? 'profile' : initialTab
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [selectedBuyerIds, setSelectedBuyerIds] = useState<string[]>([]);
  const [buyerLayout, setBuyerLayout] = useState<'number' | 'seat'>('number');
  const [seatRotated, setSeatRotated] = useState(false);
  const [seatEmptyToast, setSeatEmptyToast] = useState(false);
  const [insufficientToast, setInsufficientToast] = useState(false);
  const [purchaseToast, setPurchaseToast] = useState<{
    show: boolean;
    cost: number;
    itemName: string;
  }>({ show: false, cost: 0, itemName: '' });
  const [profileDrawPhase, setProfileDrawPhase] = useState<'idle' | 'spinning' | 'reveal'>('idle');
  const [profileDrawReveal, setProfileDrawReveal] = useState<{
    storagePath: string;
    imageVersion: number;
    setName: string;
    cardName: string;
    source: ProfileCardSetSource;
    cardNumber: number;
    totalCards: number;
    revealKey: number;
  } | null>(null);
  const [profileToast, setProfileToast] = useState<'insufficient' | 'allUnlocked' | null>(null);
  const [spinCandidates, setSpinCandidates] = useState<
    Array<{ storagePath: string; imageVersion: number }>
  >([]);
  const [spinFrame, setSpinFrame] = useState(0);
  const spinIntervalRef = useRef<number | null>(null);
  const spinTimeoutRef = useRef<number | null>(null);

  const clearProfileDrawTimers = () => {
    if (spinIntervalRef.current != null) {
      window.clearInterval(spinIntervalRef.current);
      spinIntervalRef.current = null;
    }
    if (spinTimeoutRef.current != null) {
      window.clearTimeout(spinTimeoutRef.current);
      spinTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) return;
    clearProfileDrawTimers();
    setActiveTab(profileOnly && !hideProfileCategory ? 'profile' : hideProfileCategory ? 'item' : initialTab);
    setSelectedId(null);
    setSelectedSetId(null);
    setSelectedBuyerIds([]);
    setBuyerLayout('number');
    setSeatRotated(false);
    setSeatEmptyToast(false);
    setInsufficientToast(false);
    setPurchaseToast({ show: false, cost: 0, itemName: '' });
    setProfileDrawPhase('idle');
    setProfileDrawReveal(null);
    setProfileToast(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, memberLabel, initialTab, profileOnly]);

  useEffect(() => clearProfileDrawTimers, []);

  const selectedCostForBuyers =
    activeTab === 'profile' || profileOnly
      ? profileSets.find((s) => profileCardSetKey(s.source, s.id) === selectedSetId)?.priceCoins ?? 0
      : storeItems.find((c) => c.itemCardId === selectedId)?.cost ?? 0;

  useEffect(() => {
    if (!classWide) return;
    setSelectedBuyerIds((prev) =>
      prev.filter((id) => {
        const b = buyers.find((x) => x.id === id);
        return !!b && b.coins >= selectedCostForBuyers;
      })
    );
  }, [buyers, classWide, selectedCostForBuyers]);

  const buyerSeatView = useMemo(
    () => buildStoreBuyerSeatView(buyers, seatRotated),
    [buyers, seatRotated]
  );

  const applyBuyerLayout = (mode: 'number' | 'seat') => {
    if (mode === 'seat' && buyerSeatView.positioned.length === 0) {
      setBuyerLayout('number');
      setSeatEmptyToast(true);
      window.setTimeout(() => setSeatEmptyToast(false), 2500);
      return;
    }
    setBuyerLayout(mode);
    setSeatEmptyToast(false);
  };

  if (!open) return null;

  const selected = storeItems.find((c) => c.itemCardId === selectedId) ?? null;
  const selectedCost =
    selected && typeof selected.cost === 'number' ? selected.cost : 0;
  const affordableBuyerIds = new Set(
    buyers.filter((b) => b.coins >= selectedCostForBuyers).map((b) => b.id)
  );

  const toggleBuyer = (buyerId: string) => {
    if (!affordableBuyerIds.has(buyerId)) return;
    setSelectedBuyerIds((prev) =>
      prev.includes(buyerId) ? prev.filter((id) => id !== buyerId) : [...prev, buyerId]
    );
  };

  const renderBuyerCard = (buyer: StoreBuyerOption) => {
    const canAfford = affordableBuyerIds.has(buyer.id);
    const picked = selectedBuyerIds.includes(buyer.id);
    return (
      <button
        key={buyer.id}
        type="button"
        className={
          'cp-pin-store-buyer' +
          (picked ? ' is-selected' : '') +
          (!canAfford ? ' is-disabled' : '')
        }
        disabled={!canAfford}
        aria-pressed={picked}
        onClick={() => toggleBuyer(buyer.id)}
      >
        <span className="cp-pin-store-buyer-avatar">
          <MemberProfileThumb
            member={buyer.member}
            workspaceId={workspaceId}
            gradeId={gradeId}
            className="cp-pin-store-buyer-avatar-img"
            fallback={
              <span className="cp-pin-store-buyer-fallback">
                {(buyer.name || '?').slice(0, 1)}
              </span>
            }
          />
        </span>
        <span className="cp-pin-store-buyer-id">
          <span className="cp-pin-store-buyer-no">
            {String(buyer.orderIndex).padStart(2, '0')}
          </span>
          <span className="cp-pin-store-buyer-name">{buyer.name || '(이름 없음)'}</span>
        </span>
      </button>
    );
  };

  const handlePurchase = () => {
    if (!selected) return;
    
    if (classWide) {
      // 클래스 스토어: 선택된 멤버들에게 즉시 구매 처리
      if (selectedBuyerIds.length === 0) return;
      onPurchaseMany?.(selected, selectedBuyerIds);
      setPurchaseToast({
        show: true,
        cost: selectedCost,
        itemName: `${selected.name || '(이름 없음)'} · ${selectedBuyerIds.length}명 구매 완료`,
      });
      setSelectedId(null);
      setSelectedBuyerIds([]);
      onClose(); // 구매 후 모달 닫기
      window.setTimeout(() => {
        setPurchaseToast({ show: false, cost: 0, itemName: '' });
      }, 1600);
      return;
    }
    
    // 개별 스토어: 코인 부족 체크 후 구매
    if (currentCoins < selectedCost) {
      setInsufficientToast(true);
      window.setTimeout(() => setInsufficientToast(false), 2500);
      return;
    }
    onPurchase(selected);
    setPurchaseToast({
      show: true,
      cost: selectedCost,
      itemName: selected.name || '(이름 없음)',
    });
    setSelectedId(null);
    window.setTimeout(() => {
      setPurchaseToast({ show: false, cost: 0, itemName: '' });
    }, 1600);
  };

  const handleAddToCart = () => {
    if (!selected) return;
    
    if (classWide) {
      // 클래스 스토어: 선택된 멤버들에게 담기
      if (selectedBuyerIds.length === 0) return;
      onAddToCartMany?.(selected, selectedBuyerIds);
      setPurchaseToast({
        show: true,
        cost: 0,
        itemName: `${selected.name || '(이름 없음)'} · ${selectedBuyerIds.length}명 장바구니 담기`,
      });
      setSelectedId(null);
      setSelectedBuyerIds([]);
      onClose();
    } else {
      // 개별 스토어
      onAddToCart?.(selected);
      setPurchaseToast({
        show: true,
        cost: 0,
        itemName: `${selected.name || '(이름 없음)'} 장바구니 담기`,
      });
      setSelectedId(null);
    }
    
    window.setTimeout(() => {
      setPurchaseToast({ show: false, cost: 0, itemName: '' });
    }, 1600);
  };

  const renderThumb = (item: ItemCard, size = 48) => {
    const src = resolveItemCardImageSrc(item.cardImage);
    if (src) {
      return <img src={src} alt="" />;
    }
    return <CardPlaceholder size={size} />;
  };

  const selectedSet = profileSets.find((s) => profileCardSetKey(s.source, s.id) === selectedSetId) ?? null;

  /** 카드가 여러 장 빠르게 바뀌며 도는 연출 시간(ms) — 이후 실제 뽑힌 카드를 공개 */
  const PROFILE_DRAW_SPIN_MS = 2000;
  const PROFILE_DRAW_SPIN_TICK_MS = 90;

  const startProfileReveal = (result: Extract<ProfileCardPurchaseResult, { ok: true }>) => {
    const totalCards = selectedSet ? selectedSet.slots.filter((s) => s.storagePath).length : 0;
    const candidates = (selectedSet?.slots ?? [])
      .filter((s) => Boolean(s.storagePath))
      .map((s) => ({ storagePath: s.storagePath as string, imageVersion: s.imageVersion }));
    clearProfileDrawTimers();
    setSpinCandidates(
      candidates.length > 0
        ? candidates
        : [{ storagePath: result.storagePath, imageVersion: result.imageVersion }]
    );
    setSpinFrame(0);
    setProfileDrawReveal({
      storagePath: result.storagePath,
      imageVersion: result.imageVersion,
      setName: selectedSet?.name ?? '',
      cardName: result.cardName || profileCardSlotDisplayName(null, result.slotIndex),
      source: result.source,
      cardNumber: result.slotIndex,
      totalCards,
      revealKey: Date.now(),
    });
    setProfileDrawPhase('spinning');
    spinIntervalRef.current = window.setInterval(() => {
      setSpinFrame((f) => f + 1);
    }, PROFILE_DRAW_SPIN_TICK_MS);
    spinTimeoutRef.current = window.setTimeout(() => {
      if (spinIntervalRef.current != null) {
        window.clearInterval(spinIntervalRef.current);
        spinIntervalRef.current = null;
      }
      setProfileDrawPhase('reveal');
    }, PROFILE_DRAW_SPIN_MS);
  };

  const handleProfilePurchase = () => {
    if (!selectedSet) return;
    if (classWide) {
      if (selectedBuyerIds.length === 0 || !onPurchaseProfileCardMany) return;
      const batch = onPurchaseProfileCardMany(selectedSet, selectedBuyerIds);
      if (batch.okCount === 0) {
        setProfileToast('insufficient');
        window.setTimeout(() => setProfileToast(null), 2500);
        return;
      }
      setSelectedBuyerIds([]);
      if (batch.lastOk && batch.okCount === 1) {
        startProfileReveal(batch.lastOk);
        return;
      }
      setPurchaseToast({
        show: true,
        cost: selectedSet.priceCoins,
        itemName: `${selectedSet.name} · ${batch.okCount}명`,
      });
      window.setTimeout(() => {
        setPurchaseToast({ show: false, cost: 0, itemName: '' });
      }, 1600);
      return;
    }
    if (!onPurchaseProfileCard) return;
    const result = onPurchaseProfileCard(selectedSet);
    if (!result.ok) {
      setProfileToast(result.reason === 'INSUFFICIENT_COINS' ? 'insufficient' : 'allUnlocked');
      window.setTimeout(() => setProfileToast(null), 2500);
      return;
    }
    startProfileReveal(result);
  };

  const closeProfileDrawReveal = () => {
    if (profileDrawPhase !== 'reveal') return;
    clearProfileDrawTimers();
    setProfileDrawPhase('idle');
    setProfileDrawReveal(null);
  };

  return createPortal(
    <div className="cp-apn-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="cp-apn-modal cp-pin-store-modal"
        data-tutorial-pin="store-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-pin-store-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cp-apn-modal-header">
          <h2 id="cp-pin-store-title">{profileOnly ? '프로필 카드 스토어' : '스토어'}</h2>
          <button type="button" className="cp-apn-icon-btn" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </header>

        {!profileOnly && !hideProfileCategory ? (
          <div className="cp-pin-bar-tabs cp-pin-bar-tabs--2" role="tablist">
            <button
              type="button"
              className={`cp-pin-bar-tab${activeTab === 'item' ? ' is-active' : ''}`}
              role="tab"
              aria-selected={activeTab === 'item'}
              onClick={() => setActiveTab('item')}
            >
              아이템 카드 구입
            </button>
            <button
              type="button"
              className={`cp-pin-bar-tab${activeTab === 'profile' ? ' is-active' : ''}`}
              role="tab"
              aria-selected={activeTab === 'profile'}
              onClick={() => setActiveTab('profile')}
            >
              프로필 카드 구입
            </button>
          </div>
        ) : null}

        <div className="cp-pin-store-body">
          {!hideProfileCategory && (activeTab === 'profile' || profileOnly) ? (
            profileSets.length === 0 ? (
              <div className="cp-pin-store-empty">
                <p>판매 중인 프로필 카드 세트가 없습니다</p>
                <p className="cp-pin-store-empty-hint">
                  아이템 카드 설정의 “프로필 카드” 탭에서 세트를 활성화해주세요.
                </p>
              </div>
            ) : (
              <div className="cp-pin-store-grid cp-pin-store-grid--profile">
                {profileSets.map((set) => {
                  const setKey = profileCardSetKey(set.source, set.id);
                  const cover = [...set.slots].sort(
                    (a, b) => a.slotIndex - b.slotIndex
                  )[0];
                  const isSelected = selectedSetId === setKey;
                  const kindCount = set.slots.length;
                  return (
                    <div key={setKey} className="cp-pin-profile-set-stack">
                      <button
                        type="button"
                        className={`cp-pin-profile-set-card cp-pin-profile-store-card${
                          isSelected ? ' is-selected' : ''
                        }`}
                        onClick={() => setSelectedSetId(isSelected ? null : setKey)}
                      >
                        <span className="cp-pin-profile-set-hang" aria-hidden />
                        <div className="cp-pin-profile-set-head">
                          <div className="cp-pin-profile-set-name">
                            <span>{set.name}</span>
                          </div>
                        </div>
                        <div className="cp-pin-profile-set-thumb">
                          {cover?.storagePath ? (
                            <ProfileSetCoverImg
                              source={set.source}
                              storagePath={cover.storagePath}
                              imageVersion={cover.imageVersion}
                            />
                          ) : (
                            <span className="cp-pin-profile-set-thumb-ph">?</span>
                          )}
                          {isSelected ? (
                            <span className="cp-pin-store-card-check" aria-hidden>
                              ✓
                            </span>
                          ) : null}
                        </div>
                        <div className="cp-pin-profile-set-foot">
                          <span className="cp-pin-profile-set-kind-count">
                            총 {kindCount} 종류
                          </span>
                          <span className="cp-pin-store-card-cost">
                            <CoinIcon />
                            <span>×{set.priceCoins}</span>
                          </span>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            )
          ) : storeItems.length === 0 ? (
            <div className="cp-pin-store-empty">
              <p>아이템 없음</p>
              <p className="cp-pin-store-empty-hint">
                아이템 카드 설정에서 스토어 아이템을 등록해주세요.
              </p>
            </div>
          ) : (
            <div className="cp-pin-store-grid">
              {storeItems.map((item) => {
                const cost = typeof item.cost === 'number' ? item.cost : 0;
                const selectedCard = selectedId === item.itemCardId;
                return (
                  <button
                    key={item.itemCardId}
                    type="button"
                    className={`cp-pin-store-card${selectedCard ? ' is-selected' : ''}`}
                    onClick={() =>
                      setSelectedId(selectedCard ? null : item.itemCardId)
                    }
                  >
                    <div className="cp-pin-store-card-icon-wrap">
                      <div className="cp-pin-store-card-icon">{renderThumb(item)}</div>
                      {selectedCard ? (
                        <span className="cp-pin-store-card-check" aria-hidden>
                          ✓
                        </span>
                      ) : null}
                    </div>
                    <p className="cp-pin-store-card-name">{item.name || '(이름 없음)'}</p>
                    {item.memo?.trim() ? (
                      <p className="cp-pin-store-card-memo">{item.memo}</p>
                    ) : null}
                    <div className="cp-pin-store-card-meta">
                      <span className="cp-pin-store-card-cost">
                        <CoinIcon />
                        <span>×{cost}</span>
                      </span>
                      {item.type === 'active' ? (
                        <span className="cp-pin-store-card-uses">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path
                              d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z"
                              fill="#ff4757"
                            />
                          </svg>
                          <span>×{item.useCount || 1}</span>
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {classWide ? (
            <section className="cp-pin-store-buyers" aria-label="구매자 선택">
              <div className="cp-pin-store-buyers-head">
                <h3 className="cp-pin-store-buyers-title">구매자 선택</h3>
                {buyerLayout === 'seat' ? (
                  <button
                    type="button"
                    className="cp-pin-store-buyers-rotate"
                    title="180도 회전"
                    aria-label="자리 배치 180도 회전"
                    onClick={() => setSeatRotated((v) => !v)}
                  >
                    <SeatRotateIcon size={12} />
                    <span>180</span>
                  </button>
                ) : null}
                <div
                  className={`cp-apn-card-view-toggle${buyerLayout === 'seat' ? ' is-seat' : ''}`}
                  role="group"
                  aria-label="번호 자리 보기 전환"
                >
                  <button
                    type="button"
                    className={buyerLayout === 'number' ? 'is-active' : ''}
                    onClick={() => applyBuyerLayout('number')}
                  >
                    번호
                  </button>
                  <button
                    type="button"
                    className={buyerLayout === 'seat' ? 'is-active' : ''}
                    onClick={() => applyBuyerLayout('seat')}
                  >
                    자리
                  </button>
                </div>
              </div>
              <p className="cp-pin-store-buyers-hint">
                {selectedCostForBuyers > 0
                  ? `카드 가격 ${selectedCostForBuyers}코인 미만인 멤버는 선택할 수 없습니다.`
                  : '구입할 카드를 고른 뒤 구매자를 선택하세요.'}
              </p>
              {buyerLayout === 'seat' && buyerSeatView.positioned.length > 0 ? (
                <div className="cp-pin-store-buyers-seat">
                  <div
                    className="cp-pin-store-buyers-seat-grid"
                    style={{
                      gridTemplateColumns: buyerSeatView.colTemplate,
                      gridTemplateRows: buyerSeatView.rowTemplate,
                    }}
                  >
                    {buyerSeatView.positioned.map(({ buyer, gridRow, gridCol }) => (
                      <div
                        key={buyer.id}
                        className="cp-pin-store-buyers-seat-cell"
                        style={{
                          gridRow: `${gridRow} / span 2`,
                          gridColumn: `${gridCol} / span 2`,
                        }}
                      >
                        {renderBuyerCard(buyer)}
                      </div>
                    ))}
                  </div>
                  {buyerSeatView.unseated.length > 0 ? (
                    <>
                      <p className="cp-pin-store-buyers-unseated-title">자리 미지정 멤버</p>
                      <div className="cp-pin-store-buyers-grid">
                        {buyerSeatView.unseated.map(renderBuyerCard)}
                      </div>
                    </>
                  ) : null}
                </div>
              ) : (
                <div className="cp-pin-store-buyers-grid">{buyers.map(renderBuyerCard)}</div>
              )}
            </section>
          ) : null}
        </div>

        {!hideProfileCategory && activeTab === 'profile' ? (
          <footer className="cp-pin-store-footer">
              <div className="cp-pin-store-coins">
                {classWide ? (
                  <>
                    선택:
                    <strong>{selectedBuyerIds.length}</strong>명
                  </>
                ) : (
                  <>
                    보유 코인:
                    <CoinIcon />
                    <strong>{currentCoins}</strong>
                  </>
                )}
              </div>
              <button
                type="button"
                className="cp-pin-store-buy-btn"
                disabled={
                  !selectedSet ||
                  profileDrawPhase !== 'idle' ||
                  (classWide && selectedBuyerIds.length === 0)
                }
                onClick={handleProfilePurchase}
              >
                구매하기
              </button>
            </footer>
        ) : (
          <footer className="cp-pin-store-footer">
            <div className="cp-pin-store-coins">
              {classWide ? (
                <>
                  선택:
                  <strong>{selectedBuyerIds.length}</strong>명
                </>
              ) : (
                <>
                  보유 코인:
                  <CoinIcon />
                  <strong>{currentCoins}</strong>
                </>
              )}
            </div>
            {classWide ? (
              <div className="cp-pin-store-actions">
                <button
                  type="button"
                  className="cp-pin-store-cart-btn"
                  disabled={!selected || selectedBuyerIds.length === 0}
                  onClick={handleAddToCart}
                >
                  장바구니 담기
                </button>
                <button
                  type="button"
                  className="cp-pin-store-buy-btn"
                  data-tutorial-pin="store-buy"
                  disabled={!selected || selectedBuyerIds.length === 0}
                  onClick={handlePurchase}
                >
                  구매하기
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="cp-pin-store-buy-btn"
                data-tutorial-pin="store-buy"
                disabled={!selected}
                onClick={handlePurchase}
              >
                구매하기
              </button>
            )}
          </footer>
        )}

        {insufficientToast ? (
          <div className="cp-pin-store-toast is-warn" role="status">
            코인이 부족합니다.
          </div>
        ) : null}
        {purchaseToast.show ? (
          <div className="cp-pin-store-toast is-ok" role="status">
            <strong>{purchaseToast.itemName}</strong>
            {purchaseToast.cost > 0 ? (
              <>
                <span> 구매 예정 · {purchaseToast.cost}코인</span>
              </>
            ) : null}
          </div>
        ) : null}
        {profileToast === 'insufficient' ? (
          <div className="cp-pin-store-toast is-warn" role="status">
            코인이 부족합니다.
          </div>
        ) : null}
        {profileToast === 'allUnlocked' ? (
          <div className="cp-pin-store-toast is-warn" role="status">
            뽑을 수 있는 카드가 없습니다.
          </div>
        ) : null}
        {seatEmptyToast ? (
          <div className="cp-pin-store-toast is-warn" role="status">
            {STORE_SEAT_EMPTY}
          </div>
        ) : null}
        {profileDrawReveal && profileDrawPhase !== 'idle'
          ? createPortal(
              (() => {
                const isSpinning = profileDrawPhase === 'spinning';
                const spinShot =
                  spinCandidates.length > 0
                    ? spinCandidates[spinFrame % spinCandidates.length]
                    : null;
                const shownStoragePath = isSpinning
                  ? (spinShot?.storagePath ?? profileDrawReveal.storagePath)
                  : profileDrawReveal.storagePath;
                const shownImageVersion = isSpinning
                  ? (spinShot?.imageVersion ?? profileDrawReveal.imageVersion)
                  : profileDrawReveal.imageVersion;
                return (
                  <div
                    className="cp-pin-profile-draw-reveal"
                    role="status"
                    onClick={closeProfileDrawReveal}
                  >
                    {!isSpinning ? <ConfettiBurst animKey={profileDrawReveal.revealKey} /> : null}
                    <div
                      key={isSpinning ? 'spinning' : profileDrawReveal.revealKey}
                      className={`cp-pin-profile-draw-reveal-card${isSpinning ? ' is-spinning' : ' is-settled'}`}
                    >
                      <div className="cp-pin-profile-draw-reveal-card-image">
                        <ProfileSetCoverImg
                          source={profileDrawReveal.source}
                          storagePath={shownStoragePath}
                          imageVersion={shownImageVersion}
                        />
                      </div>
                      <div className="cp-pin-profile-draw-reveal-card-footer">
                        <span className="cp-pin-profile-draw-reveal-card-number">
                          {isSpinning ? '???' : profileDrawReveal.cardName}
                        </span>
                      </div>
                    </div>
                    <p
                      className={`cp-pin-profile-draw-reveal-text${isSpinning ? ' is-spinning' : ''}`}
                    >
                      {isSpinning ? (
                        '카드를 뽑는 중\u2026'
                      ) : (
                        <>
                          <strong>{profileDrawReveal.setName}</strong>
                          {' · '}
                          <strong>{profileDrawReveal.cardName}</strong> 획득!
                        </>
                      )}
                    </p>
                    <p className="cp-pin-profile-draw-reveal-hint">
                      {isSpinning ? '\u00A0' : '화면을 눌러 닫기'}
                    </p>
                  </div>
                );
              })(),
              document.body
            )
          : null}
      </div>
    </div>,
    document.body
  );
};

export default MemberStoreModal;
